// Compact page-level job status row, design §5 table: "a compact job status
// row, followed by existing sync/disconnected-history notice... A known
// running job stays visible even with the tools menu closed." Renders
// nothing when no job is tracked. Every branch below is a literal row from
// the §5 table -- no invented percent/ETA, no missing-count-as-zero.
import { useEffect, useState } from 'react';
import { Button } from '@moneyskyline/ui';
import { useRecategorize } from '@/api/hooks/useRecategorize';
import { dismissJob, parseCountField, registerJob, useTrackedJobs } from './jobRegistry';

function formatTime(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function JobStatusRow({ onViewProgress }: { onViewProgress?: () => void }) {
  const { activeJob } = useTrackedJobs();
  const recategorize = useRecategorize();
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null);
  const [hadConfirmedStatus, setHadConfirmedStatus] = useState(false);
  const [restartError, setRestartError] = useState(false);

  // Stamp "last checked" and remember whether a confirmed status was ever
  // seen -- outside render, per React's rules-of-hooks, not as a
  // during-render side effect.
  useEffect(() => {
    if (!activeJob) return;
    // Stamping a wall-clock "last checked" time is synchronizing with the
    // external poll clock, not derived render state -- see ProtectedRoute.tsx
    // for the same accepted pattern.
    if (!activeJob.pollError && activeJob.hasData) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLastCheckedAt(new Date());
    }
    if (activeJob.status) setHadConfirmedStatus(true);
  }, [activeJob]);

  if (!activeJob) return null;

  if (activeJob.notFound) {
    return (
      <div className="flex items-center gap-3 rounded-md border border-border bg-muted px-3 py-2 text-sm" role="status" data-testid="job-status-row-not-found">
        <span>This review&rsquo;s status is unavailable.</span>
        <Button type="button" size="sm" variant="ghost" onClick={() => dismissJob(activeJob.jobId)}>
          Dismiss
        </Button>
      </div>
    );
  }

  function restart() {
    setRestartError(false);
    recategorize.mutate(
      { scope: 'all' },
      {
        onSuccess: (data) => {
          if (activeJob) dismissJob(activeJob.jobId);
          registerJob(data.job_id, 'tools');
        },
        onError: () => setRestartError(true),
      },
    );
  }

  // Polling itself is failing -- distinct from any job status (§5 "Polling unavailable").
  if (activeJob.pollError) {
    return (
      <div className="flex items-center gap-3 rounded-md border border-border bg-muted px-3 py-2 text-sm" role="status" data-testid="job-status-row-poll-error">
        <span>Can&rsquo;t check progress. The review may still be running.</span>
        {hadConfirmedStatus && lastCheckedAt && (
          <span className="text-xs text-muted-foreground">Last checked {formatTime(lastCheckedAt)}</span>
        )}
        <Button type="button" size="sm" variant="outline" onClick={activeJob.refetch}>
          Check again
        </Button>
      </div>
    );
  }

  if (!activeJob.hasData) return null;
  const status = activeJob.status;

  if (status === 'queued') {
    return (
      <div className="flex items-center gap-3 rounded-md border border-border bg-muted px-3 py-2 text-sm" role="status" data-testid="job-status-row-queued">
        <span>Reclassification queued</span>
        <Button type="button" size="sm" variant="outline" onClick={onViewProgress}>
          View progress
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => dismissJob(activeJob.jobId)}>
          Close
        </Button>
      </div>
    );
  }

  if (status === 'running') {
    const candidateCount = parseCountField(activeJob.progress, 'candidate_count');
    const processed = parseCountField(activeJob.progress, 'processed');
    const validCounts = candidateCount !== null && processed !== null && processed <= candidateCount;
    const invalidRange = candidateCount !== null && processed !== null && processed > candidateCount;
    if (invalidRange) {
      // Internal validation-failure log, not user-facing (§5).
      console.warn('[JobStatusRow] processed > candidate_count for job', activeJob.jobId, { processed, candidateCount });
    }
    if (validCounts) {
      return (
        <div className="flex items-center gap-3 rounded-md border border-border bg-muted px-3 py-2 text-sm" role="status" data-testid="job-status-row-running">
          <span>Reviewing categories · {processed} of {candidateCount} items checked</span>
          <Button type="button" size="sm" variant="outline" onClick={onViewProgress}>
            View progress
          </Button>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-3 rounded-md border border-border bg-muted px-3 py-2 text-sm" role="status" aria-busy="true" data-testid="job-status-row-running-indeterminate">
        <span>{invalidRange ? 'Progress counts unavailable' : 'Reviewing categories…'}</span>
        <Button type="button" size="sm" variant="outline" onClick={onViewProgress}>
          View progress
        </Button>
      </div>
    );
  }

  if (status === 'done') {
    const changed = parseCountField(activeJob.progress, 'changed');
    const isMissing = activeJob.progress ? !('changed' in activeJob.progress) || changed === null : true;
    const message = isMissing
      ? 'Review complete. Change count unavailable.'
      : changed === 0
        ? 'Review complete · Nothing changed'
        : `Review complete · ${changed} items updated`;
    const testId = isMissing ? 'job-status-row-done-unavailable' : changed === 0 ? 'job-status-row-done-empty' : 'job-status-row-done';
    return (
      <div className="flex items-center gap-3 rounded-md border border-border bg-muted px-3 py-2 text-sm" role="status" data-testid={testId}>
        <span>{message}</span>
        <Button type="button" size="sm" variant="outline" onClick={restart} disabled={recategorize.isPending}>
          Reclassify again
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => dismissJob(activeJob.jobId)}>
          Dismiss
        </Button>
        {restartError && (
          <span role="alert" className="text-xs text-degraded-foreground" data-testid="job-status-row-restart-error">
            Couldn&rsquo;t start reclassification.
          </span>
        )}
      </div>
    );
  }

  if (status === 'failed') {
    const errorCode = activeJob.error;
    const isNotConfigured = errorCode === 'llm_not_configured';
    const message = isNotConfigured
      ? "Automatic categorization isn't available right now."
      : 'Reclassification stopped before finishing.';
    const processed = parseCountField(activeJob.progress, 'processed');
    return (
      <div className="flex items-center gap-3 rounded-md border border-degraded-border bg-degraded-surface px-3 py-2 text-sm text-degraded-foreground" role="alert" data-testid="job-status-row-failed">
        <span>
          {message}
          {!isNotConfigured && processed !== null && ` ${processed} items were checked before it stopped. Some items may already have changed.`}
        </span>
        <Button type="button" size="sm" variant="outline" onClick={isNotConfigured ? activeJob.refetch : restart} disabled={recategorize.isPending}>
          {isNotConfigured ? 'Check again' : 'Try again'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => dismissJob(activeJob.jobId)}>
          Dismiss
        </Button>
        {restartError && (
          <span role="alert" className="text-xs" data-testid="job-status-row-restart-error">
            Couldn&rsquo;t start reclassification.
          </span>
        )}
      </div>
    );
  }

  if (status === 'cancelled') {
    return (
      <div className="flex items-center gap-3 rounded-md border border-border bg-muted px-3 py-2 text-sm" role="status" data-testid="job-status-row-cancelled">
        <span>Reclassification cancelled.</span>
        <Button type="button" size="sm" variant="outline" onClick={restart} disabled={recategorize.isPending}>
          Start again
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => dismissJob(activeJob.jobId)}>
          Dismiss
        </Button>
        {restartError && (
          <span role="alert" className="text-xs text-degraded-foreground" data-testid="job-status-row-restart-error">
            Couldn&rsquo;t start reclassification.
          </span>
        )}
      </div>
    );
  }

  return null;
}

export default JobStatusRow;
